import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  contrastRatio,
  hslToRgb,
  parseColor,
  randomInt,
  rgbToHex,
  rgbToHsl,
  type RGB,
} from "@/lib/utilfns";
import { Card } from "@/components/ui/primitives";
import { CopyButton, OutputPane, inputCls, labelCls } from "./engines";

function fmt(rgb: RGB) {
  const { h, s, l } = rgbToHsl(rgb);
  return {
    hex: rgbToHex(rgb),
    rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
    hsl: `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`,
  };
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const valid = useMemo(() => {
    try {
      return rgbToHex(parseColor(value));
    } catch {
      return null;
    }
  }, [value]);
  return (
    <label className="block flex-1">
      <span className={labelCls}>{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={valid ?? "#888888"}
          onChange={(e) => onChange(e.target.value)}
          className="h-[34px] w-12 cursor-pointer rounded-lg border border-app bg-surface-2 p-1"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#f59e0b · rgb(245, 158, 11) · hsl(38, 92%, 50%)"
          className={cn(inputCls, "font-mono")}
          spellCheck={false}
        />
      </div>
    </label>
  );
}

/* ── Color converter ──────────────────────────────────────────────────────── */

export function ColorConverter() {
  const [input, setInput] = useState("#f59e0b");
  const parsed = useMemo(() => {
    try {
      return { rgb: parseColor(input), error: null as string | null };
    } catch (e) {
      return { rgb: null, error: (e as Error).message };
    }
  }, [input]);

  return (
    <div className="space-y-4">
      <ColorField label="Color" value={input} onChange={setInput} />
      {parsed.error && <div className="font-mono text-xs text-rose-400">{parsed.error}</div>}
      {parsed.rgb && (
        <>
          <div
            className="h-24 rounded-2xl border border-app"
            style={{ background: rgbToHex(parsed.rgb) }}
          />
          <Card className="divide-y divide-app/40">
            {Object.entries(fmt(parsed.rgb)).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-[11px] uppercase tracking-wider text-app-2">{k}</span>
                <span className="flex items-center gap-2">
                  <span className="select-all font-mono text-xs font-medium text-app">{v}</span>
                  <CopyButton text={v} />
                </span>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}

/* ── Contrast checker ─────────────────────────────────────────────────────── */

export function ContrastChecker() {
  const [fg, setFg] = useState("#111827");
  const [bg, setBg] = useState("#f59e0b");
  const result = useMemo(() => {
    try {
      const f = parseColor(fg);
      const b = parseColor(bg);
      return { ratio: contrastRatio(f, b), f, b, error: null as string | null };
    } catch (e) {
      return { ratio: 0, f: null, b: null, error: (e as Error).message };
    }
  }, [fg, bg]);

  const grade = (min: number) => (result.ratio >= min ? "pass" : "fail");
  const badges: [string, number][] = [
    ["AA normal (4.5:1)", 4.5],
    ["AA large (3:1)", 3],
    ["AAA normal (7:1)", 7],
    ["AAA large (4.5:1)", 4.5],
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <ColorField label="Text" value={fg} onChange={setFg} />
        <ColorField label="Background" value={bg} onChange={setBg} />
      </div>
      {result.error ? (
        <div className="font-mono text-xs text-rose-400">{result.error}</div>
      ) : (
        result.f &&
        result.b && (
          <>
            <div
              className="grid h-28 place-items-center rounded-2xl border border-app text-center"
              style={{ background: rgbToHex(result.b), color: rgbToHex(result.f) }}
            >
              <div>
                <div className="text-2xl font-semibold">Aa — {result.ratio.toFixed(2)}:1</div>
                <div className="text-xs opacity-80">The quick brown fox jumps over the lazy dog</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {badges.map(([label, min]) => (
                <Card key={label} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-app-1">{label}</span>
                  <span
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase",
                      grade(min) === "pass"
                        ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-500"
                        : "border-rose-500/30 bg-rose-500/15 text-rose-400"
                    )}
                  >
                    {grade(min)}
                  </span>
                </Card>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}

/* ── Swatch strip shared by shades & palette ──────────────────────────────── */

function SwatchRow({ colors }: { colors: string[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${colors.length}, 1fr)` }}>
      {colors.map((c, i) => (
        <button
          key={`${c}-${i}`}
          onClick={() => {
            navigator.clipboard?.writeText(c);
            setCopied(c);
            setTimeout(() => setCopied(null), 1000);
          }}
          className="group overflow-hidden rounded-xl border border-app text-left transition hover:border-app-strong"
          title={`Copy ${c}`}
        >
          <div className="h-16" style={{ background: c }} />
          <div className="bg-surface-2 px-2 py-1.5 text-center font-mono text-[10px] text-app-1">
            {copied === c ? "Copied!" : c}
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── Shades generator ─────────────────────────────────────────────────────── */

export function ShadesGenerator() {
  const [input, setInput] = useState("#f59e0b");
  const shades = useMemo(() => {
    try {
      const { h, s } = rgbToHsl(parseColor(input));
      const stops = [0.97, 0.9, 0.8, 0.68, 0.56, 0.47, 0.39, 0.31, 0.24, 0.17];
      return { colors: stops.map((l) => rgbToHex(hslToRgb(h, s, l))), error: null as string | null };
    } catch (e) {
      return { colors: [], error: (e as Error).message };
    }
  }, [input]);
  return (
    <div className="space-y-4">
      <ColorField label="Base color" value={input} onChange={setInput} />
      {shades.error ? (
        <div className="font-mono text-xs text-rose-400">{shades.error}</div>
      ) : (
        <>
          <SwatchRow colors={shades.colors} />
          <p className="text-[11px] text-app-2">
            50 → 900 scale (lightness ramp at constant hue/saturation). Click a swatch to copy.
          </p>
        </>
      )}
    </div>
  );
}

/* ── Palette generator ────────────────────────────────────────────────────── */

export function PaletteGenerator() {
  const make = () => {
    const h = randomInt(0, 359);
    const scheme = randomInt(0, 2);
    const hues =
      scheme === 0
        ? [h, h + 30, h + 60, h + 180, h + 210] // analogous + complement
        : scheme === 1
          ? [h, h + 120, h + 240, h + 60, h + 180] // triadic spread
          : [h, h + 15, h + 165, h + 180, h + 195]; // split complementary
    return hues.map((hh, i) => rgbToHex(hslToRgb(hh % 360, 0.55 + (i % 2) * 0.15, 0.42 + i * 0.09)));
  };
  const [colors, setColors] = useState<string[]>(make);
  return (
    <div className="space-y-4">
      <button
        onClick={() => setColors(make())}
        className="flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
      >
        <RefreshCw size={13} /> New palette
      </button>
      <SwatchRow colors={colors} />
      <OutputPane text={colors.join("\n")} minHeight={64} />
    </div>
  );
}

/* ── Gradient generator ───────────────────────────────────────────────────── */

export function GradientGenerator() {
  const [from, setFrom] = useState("#f59e0b");
  const [to, setTo] = useState("#f43f5e");
  const [angle, setAngle] = useState(135);
  const css = useMemo(() => {
    try {
      const a = rgbToHex(parseColor(from));
      const b = rgbToHex(parseColor(to));
      return { value: `background: linear-gradient(${angle}deg, ${a}, ${b});`, bg: `linear-gradient(${angle}deg, ${a}, ${b})`, error: null as string | null };
    } catch (e) {
      return { value: "", bg: "", error: (e as Error).message };
    }
  }, [from, to, angle]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <ColorField label="From" value={from} onChange={setFrom} />
        <ColorField label="To" value={to} onChange={setTo} />
      </div>
      <label className="block">
        <span className={labelCls}>Angle · {angle}°</span>
        <input
          type="range"
          min={0}
          max={360}
          value={angle}
          onChange={(e) => setAngle(Number(e.target.value))}
          className="w-full accent-amber-500"
        />
      </label>
      {css.error ? (
        <div className="font-mono text-xs text-rose-400">{css.error}</div>
      ) : (
        <>
          <div className="h-28 rounded-2xl border border-app" style={{ background: css.bg }} />
          <OutputPane text={css.value} minHeight={48} />
        </>
      )}
    </div>
  );
}

/* ── Box shadow generator ─────────────────────────────────────────────────── */

export function BoxShadowGenerator() {
  const [x, setX] = useState(0);
  const [y, setY] = useState(12);
  const [blur, setBlur] = useState(32);
  const [spread, setSpread] = useState(-8);
  const [alpha, setAlpha] = useState(35);
  const shadow = `${x}px ${y}px ${blur}px ${spread}px rgba(0, 0, 0, ${(alpha / 100).toFixed(2)})`;
  const sliders: [string, number, (n: number) => void, number, number][] = [
    ["Offset X", x, setX, -48, 48],
    ["Offset Y", y, setY, -48, 48],
    ["Blur", blur, setBlur, 0, 96],
    ["Spread", spread, setSpread, -48, 48],
    ["Opacity %", alpha, setAlpha, 0, 100],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        {sliders.map(([label, v, set, min, max]) => (
          <label key={label} className="block">
            <span className={labelCls}>
              {label} · {v}
            </span>
            <input
              type="range"
              min={min}
              max={max}
              value={v}
              onChange={(e) => set(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
          </label>
        ))}
      </div>
      <div className="grid h-40 place-items-center rounded-2xl border border-app bg-surface-2">
        <div
          className="grid h-20 w-40 place-items-center rounded-xl bg-surface text-xs text-app-2"
          style={{ boxShadow: shadow }}
        >
          preview
        </div>
      </div>
      <OutputPane text={`box-shadow: ${shadow};`} minHeight={48} />
    </div>
  );
}
